<template>
  <FilterChip
    :is-text="showsText"
    :label="chipLabel"
    :icon="icon"
    :badge="badge"
    :tooltip="showLabelOnFace ? null : label"
    :active="active"
    has-popup
    :open="open"
    :pop-title="label"
    :wide="wide"
    :show-clear="active"
    @toggle="open = !open"
    @close="open = false"
    @clear="onClear"
  >
    <div v-if="searchable" class="filter-multi-chip__search">
      <BaseIcon name="search" class="filter-multi-chip__search-ico" />
      <input
        ref="search"
        v-model="query"
        type="text"
        class="filter-multi-chip__search-input"
        :placeholder="searchPlaceholder || $t('filters.search')"
      />
    </div>
    <FilterCheckboxList :options="visibleItems" :value="value" @change="$emit('change', $event)">
      <template v-if="$scopedSlots.option" #option="slotProps">
        <slot name="option" v-bind="slotProps" />
      </template>
    </FilterCheckboxList>
    <p v-if="searchable && !visibleItems.length" class="filter-multi-chip__empty">{{ $t('filters.no-results') }}</p>
    <template v-if="$scopedSlots['title-action']" #title-action>
      <slot name="title-action" />
    </template>
  </FilterChip>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import FilterChip from '@/components/filters/FilterChip';
import FilterCheckboxList from '@/components/filters/FilterCheckboxList';
import { LABEL_MODE, LABEL_MODES, normalizeLabelMode } from '@/components/filters/label-mode';
import { normalizeStr } from '@/util/string-util';

/**
 * Generic multi-select filter chip: FilterChip shell + FilterCheckboxList content.
 * Presentational — `:label :icon :items :value` in, `change`(array)/`clear` out. It owns its
 * own popover open state (independent chips coordinate via the popover's click-outside close).
 * Custom option rows (platform icon/tags/…) ride the `#option` scoped slot. An optional
 * `#title-action` slot is forwarded to FilterChip (right side of the popover title row). For
 * single-choice dimensions, use FilterSingleChip instead (see ReadChip/FollowedChip/SharedChip).
 * `searchable` adds a filter input above the list (accent-insensitive contains) for long
 * option sets (e.g. emitters).
 */
export default {
  name: 'FilterMultiChip',
  components: { BaseIcon, FilterChip, FilterCheckboxList },
  inject: { labelled: { from: 'filterChipsLabelled', default: false } },
  props: {
    label: { type: String, required: true },
    icon: { type: String, default: null },
    // Text chips (status/platform/…) show the label + selection in the chip face; icon chips
    // (read/followed/…) show an icon + a count badge instead.
    isText: { type: Boolean, default: false },
    items: { type: Array, default: () => [] }, // [{ value, text }]
    value: { type: Array, default: () => [] },
    wide: { type: Boolean, default: false },
    // Label shown once every option is selected; defaults to the shared "All".
    allLabel: { type: String, default: null },
    // When true, a single selection still reads as "(1)" instead of " : value" — used by
    // dimensions (platforms) whose values are long/branded and read better as a count.
    countOnly: { type: Boolean, default: false },
    // How the label behaves on a text chip — see label-mode.js. Unknown values clamp to 'full'.
    labelMode: {
      type: String,
      default: LABEL_MODE.FULL,
      validator: (v) => LABEL_MODES.includes(v),
    },
    // Adds a search input above the option list, filtering it as the user types.
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: null },
  },
  data() {
    return { open: false, query: '' };
  },
  computed: {
    active() {
      return this.value.length > 0;
    },
    // In a phone's list of filters or line of filters in use (FilterChipsContext), a chip
    // names what it filters, whatever it shows elsewhere.
    showsText() {
      return this.isText || this.labelled;
    },
    // Resolved mode with unknown values clamped to the safe default (always the full label in
    // a phone's list or line of filters in use).
    mode() {
      return this.labelled ? LABEL_MODE.FULL : normalizeLabelMode(this.labelMode);
    },
    // The label sits on the chip face only in 'full' mode — otherwise it moves to the tooltip
    // (icon-only chips, and collapse/value chips that show only the selection once active).
    showLabelOnFace() {
      return this.showsText && this.mode === LABEL_MODE.FULL;
    },
    allText() {
      return this.allLabel || this.$t('filters.all');
    },
    isAll() {
      return this.items.length > 0 && this.value.length >= this.items.length;
    },
    badge() {
      if (!this.value.length) return '';
      return this.isAll ? `(${this.allText})` : `(${this.value.length})`;
    },
    // A single named pick reads as " : value"; multiple/all read as a "(N)"/"(All)" parenthetical.
    isNamedSingle() {
      return !this.countOnly && this.value.length === 1 && !this.isAll;
    },
    // The resolved option for a single named pick — null if it isn't in the list yet (e.g. the
    // option list is still loading on page reload). We must never fall back to the raw id.
    singleOption() {
      return this.isNamedSingle ? this.items.find((o) => o.value === this.value[0]) || null : null;
    },
    // The selection value on its own: the picked option for a single pick, else "(All)"/"(N)".
    selectionValue() {
      if (this.isAll) return `(${this.allText})`;
      if (this.isNamedSingle) return this.singleOption ? this.singleOption.text : `(${this.value.length})`;
      return `(${this.value.length})`;
    },
    chipLabel() {
      if (!this.showsText) return this.label;
      if (!this.value.length) return this.mode === LABEL_MODE.VALUE ? '' : this.label;
      // A single pick whose option hasn't loaded yet → show the label / icon, never a raw id.
      if (this.isNamedSingle && !this.singleOption) return this.mode === LABEL_MODE.VALUE ? '' : this.label;
      if (this.mode !== LABEL_MODE.FULL) return this.selectionValue;
      // 'full': "label : value" for a single pick, "label (N)"/"label (All)" otherwise.
      return this.isNamedSingle ? `${this.label} : ${this.selectionValue}` : `${this.label} ${this.selectionValue}`;
    },
    // The options matching the typed query (accent- and case-insensitive contains).
    visibleItems() {
      if (!this.searchable || !this.query.trim()) return this.items;
      const query = normalizeStr(this.query);
      return this.items.filter((item) => normalizeStr(item.text).includes(query));
    },
  },
  methods: {
    onClear() {
      this.$emit('change', []);
      this.$emit('clear');
      this.open = false;
    },
  },
  watch: {
    // Opening focuses the search input; closing resets the query so the full
    // list is back next time.
    open(open) {
      if (!this.searchable) return;
      if (!open) {
        this.query = '';
        return;
      }
      this.$nextTick(() => {
        if (this.$refs.search) this.$refs.search.focus();
      });
    },
  },
};
</script>

<style lang="scss">
.filter-multi-chip {
  &__search {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 6px;
    padding: 6px 12px 6px 8px;
    background: $color-slate-50;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    transition: border 200ms ease, box-shadow 200ms ease;

    &:focus-within {
      border-color: $color-accent;
      box-shadow: 0 0 0 3px rgba($color-accent-500, 0.1);

      .filter-multi-chip__search-ico {
        color: $color-accent;
      }
    }
  }

  &__search-ico {
    flex: none;
    width: 16px;
    height: 16px;
    color: $color-slate-400;
    transition: color 200ms ease;
  }

  &__search-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: none;
    font-size: 13px;
    color: $color-normal;

    &::placeholder {
      color: $color-text-muted;
    }
  }

  &__empty {
    margin: 0;
    padding: 4px 8px;
    font-size: $font-size-small;
    color: $color-slate-500;
  }
}
</style>
